import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { loadCourseCopy, loadedOpeningGuide } from "../../content/catalogCopy.ts";
import { CLUB_SAMPLE, OPENING_CONTEXT } from "../../content/openingContext.ts";
import { summaryBySlug } from "../../content/lookup.ts";
import { PRACTICES, practiceByMode, practiceSentence } from "../../trainer/practiceCopy.ts";
import { usePageMeta } from "../seo.ts";
import { OpeningInfographic } from "../openings/OpeningInfographic.tsx";
import { LevelTag } from "../openings/LevelTag.tsx";

export function OpeningPage() {
  const { slug = "" } = useParams();
  const course = summaryBySlug(slug);
  // The guide text downloads on demand; the header shows from the manifest at once.
  const [copyState, setCopyState] = useState<"loading" | "ready" | "error">(() =>
    course && loadedOpeningGuide(course.openingId) ? "ready" : "loading"
  );
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    loadCourseCopy().then(
      () => live && setCopyState("ready"),
      () => live && setCopyState("error")
    );
    return () => {
      live = false;
    };
  }, [attempt]);
  usePageMeta({
    title: course ? `${course.title} — The First 15 Moves` : "Opening — The First 15 Moves",
    description: course?.tagline || "Share this opening course from The First 15 Moves.",
    path: `/opening/${slug}`,
  });
  if (!course) return <Navigate to="/openings" replace />;

  const mainLine = practiceByMode("learn");
  const otherPractices = PRACTICES.filter((item) => item.mode !== "learn");
  const guide = copyState === "ready" ? loadedOpeningGuide(course.openingId) : null;
  const context = OPENING_CONTEXT[course.openingId];

  return (
    <section className="panel opening-detail">
      <Link className="opening-back-link" to="/openings">
        ← All openings
      </Link>
      <p className="eyebrow">Play as {course.side === "black" ? "Black" : "White"}</p>
      <div className="hero-board-title-row">
        <h2>{course.title}</h2>
        <span className="eco-chip">{course.eco}</span>
        <span className="coverage-chip">
          {course.reviewStatus === "draft" ? "Draft" : "Reviewed"}
        </span>
      </div>
      <p className="section-sub">{guide ? guide.intro : course.tagline}</p>
      <p className="opening-frequency">{context.frequency}</p>
      <p className="opening-sample-note">{CLUB_SAMPLE}</p>
      <p className="opening-level-focus">
        <LevelTag level={course.level} />
        <span>
          <span className="opening-focus-label">Focus</span>
          {course.focus}
        </span>
      </p>
      {guide ? (
        <>
          <OpeningInfographic
            key={course.openingId}
            guide={guide}
            side={course.side}
            title={course.title}
          />
          <div className="opening-guide-sections">
            <section className="opening-connection" aria-labelledby="opening-connection-heading">
              <h3 id="opening-connection-heading">How this connects</h3>
              <p>{context.connection}</p>
            </section>
            <section aria-labelledby="opening-learning-heading">
              <h3 id="opening-learning-heading">What you’ll learn to judge</h3>
              <ul className="opening-principles">
                {guide.principles.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </section>
            <div>
              <section aria-labelledby="opening-mainline-heading">
                <h3 id="opening-mainline-heading">Where the main line leads</h3>
                <p>{guide.continuation}</p>
              </section>
              <section aria-labelledby="opening-branches-heading">
                <h3 id="opening-branches-heading">Other branches to recognize</h3>
                <p>{guide.branches}</p>
              </section>
            </div>
          </div>
        </>
      ) : (
        <div className="opening-guide-loading" aria-busy={copyState === "loading"}>
          <p role={copyState === "error" ? "alert" : "status"}>
            {copyState === "error"
              ? "We couldn’t load this guide. Check your connection and try again."
              : "Loading the opening guide…"}
          </p>
          {copyState === "error" ? (
            <button
              className="btn-ghost btn-sm"
              type="button"
              onClick={() => {
                setCopyState("loading");
                setAttempt((n) => n + 1);
              }}
            >
              Try again
            </button>
          ) : null}
        </div>
      )}
      <div className="practice-entry">
        <Link className="btn-primary" to={`/train/${course.slug}?mode=learn`}>
          Practice as {course.side === "white" ? "White" : "Black"}
        </Link>
        <p className="practice-entry-sentence">{practiceSentence(mainLine, course.side)}</p>
        <ul className="practice-choices">
          {otherPractices.map((item) => (
            <li key={item.mode}>
              <Link className="practice-choice" to={`/train/${course.slug}?mode=${item.mode}`}>
                <span className="practice-choice-title">{item.title}</span>
                <span className="practice-choice-sentence">
                  {practiceSentence(item, course.side)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
